import {
  Button,
  ButtonVariant,
  Toolbar,
  ToolbarItem,
  ToolbarContent,
  ToolbarFilter,
  ToolbarToggleGroup,
  ToolbarGroup,
  Dropdown,
  DropdownItem,
  DropdownPosition,
  DropdownToggle,
  InputGroup,
  TextInput,
  Pagination,
  Select,
  SelectVariant,
  SelectOption
} from '@patternfly/react-core'
import { SyncAltIcon, FilterIcon } from '@patternfly/react-icons'
import { format } from 'date-fns'
import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'

import type { IPagination} from '@app/lib/pagination';
import { useHandleCurrentPageChanged } from '@app/lib/pagination'

import type {
  ToolbarChip,
  SelectOptionObject} from '@patternfly/react-core';

enum FilterCategories {
  BirthDate = 'birth_date',
  BirthSex = 'birth_sex',
  MRN = 'mrn',
  PatientName = 'patient_name',
  Deliverable = 'deliverable',
}

const FilterLabels: Record<FilterCategories, string> = {
  [FilterCategories.BirthDate]: 'Date of Birth',
  [FilterCategories.BirthSex]: 'Birth Sex',
  [FilterCategories.MRN]: 'MRN',
  [FilterCategories.PatientName]: 'Patient Name',
  [FilterCategories.Deliverable]: 'Communication Status',
}

interface Props {
  birthSex?: string[]
  onBirthSexUpdated: (birthSex?: string[]) => void
  dateOfBirth?: string
  onDateOfBirthUpdated?: (dateOfBirth?: string) => void
  mrnFilter?: string
  onMrnFilterUpdated: (mrnFilter?: string) => void
  nameFilter?: string
  onNameFilterUpdated: (nameFilters?: string) => void
  deliverableFilter?: boolean
  onDeliverableFilterUpdated?: (deliverableFilter?: boolean) => void
  onPrimaryAction?: () => void
  onRefresh: () => void
  onClearAllFilters?: () => void
  primaryActionLabel?: string
  pagination: IPagination
}

export const PatientListToolbar = ({
  birthSex,
  onBirthSexUpdated,
  dateOfBirth,
  onDateOfBirthUpdated,
  mrnFilter,
  onMrnFilterUpdated,
  nameFilter,
  onNameFilterUpdated,
  deliverableFilter,
  onDeliverableFilterUpdated,
  onPrimaryAction,
  onRefresh,
  onClearAllFilters,
  primaryActionLabel,
  pagination,
}: Props) => {
  const [isBirthSexDropdownOpen, setIsBirthSexDropdownOpen] = useState(false)
  const [dateOfBirthInput, setDateOfBirthInput] = useState('')
  const [isDeliverableDropdownOpen, setIsDeliverableDropdownOpen] = useState(
    false,
  )
  const [currentCategory, setCurrentCategory] = useState<FilterCategories>(
    FilterCategories.PatientName,
  )
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false)

  const maxDate = useMemo(() => format(new Date(), 'yyyy-MM-dd'), [])

  React.useEffect(() => {
    if (dateOfBirth) {
      setDateOfBirthInput(dateOfBirth)
    }
  }, [dateOfBirth])

  const dateOfBirthFilterChips = useMemo(
    () => (dateOfBirth ? [dateOfBirth] : []),
    [dateOfBirth],
  )

  const deliverableFilterChips = useMemo(() => {
    if (deliverableFilter !== undefined) {
      return deliverableFilter ? ['Deliverable'] : ['Undeliverable']
    }
    return []
  }, [deliverableFilter])

  const mrnFilterChips = useMemo(() => (mrnFilter ? [mrnFilter] : []), [
    mrnFilter,
  ])

  const nameFilterChips = useMemo(() => (nameFilter ? [nameFilter] : []), [
    nameFilter,
  ])

  const {
    pageNumber,
    onSetPage,
    onPerPageSelect,
    totalNumberOfRecords,
    recordsPerPage,
  } = pagination

  const handleSetPage = useCallback(
    (_event: unknown, updatedPageNumber: number) => {
      onSetPage(updatedPageNumber)
    },
    [onSetPage],
  )

  const handlePerPageSelect = useCallback(
    (_event: unknown, updatedPerPage: number) => {
      onPerPageSelect(updatedPerPage)
    },
    [onPerPageSelect],
  )

  const { handleCurrentPageChanged } = useHandleCurrentPageChanged(
    onSetPage,
    recordsPerPage,
    totalNumberOfRecords,
  )

  const onCategorySelect = useCallback((event: React.SyntheticEvent) => {
    const target = event.target as HTMLElement | null
    const value = target?.getAttribute('value') as FilterCategories | null
    if (value) {
      setCurrentCategory(value)
      setIsCategoryDropdownOpen(false)
    }
  }, [])

  const onCategoryToggle = useCallback(() => {
    setIsCategoryDropdownOpen((open) => !open)
  }, [])

  const categoryDropdownItems = useMemo(() => {
    const items = [
      <DropdownItem key={FilterCategories.MRN} value={FilterCategories.MRN}>
        {FilterLabels[FilterCategories.MRN]}
      </DropdownItem>,
      <DropdownItem
        key={FilterCategories.PatientName}
        value={FilterCategories.PatientName}
      >
        {FilterLabels[FilterCategories.PatientName]}
      </DropdownItem>,
      <DropdownItem
        key={FilterCategories.BirthSex}
        value={FilterCategories.BirthSex}
      >
        {FilterLabels[FilterCategories.BirthSex]}
      </DropdownItem>,
      <DropdownItem
        key={FilterCategories.BirthDate}
        value={FilterCategories.BirthDate}
      >
        {FilterLabels[FilterCategories.BirthDate]}
      </DropdownItem>,
    ]

    if (onDeliverableFilterUpdated) {
      items.push(
        <DropdownItem
          key={FilterCategories.Deliverable}
          value={FilterCategories.Deliverable}
        >
          {FilterLabels[FilterCategories.Deliverable]}
        </DropdownItem>,
      )
    }
    return items
  }, [onDeliverableFilterUpdated])

  const onDeleteMrnFilter = useCallback(() => {
    onMrnFilterUpdated((undefined as unknown) as string)
  }, [onMrnFilterUpdated])

  const onDeleteNameFilter = useCallback(() => {
    onNameFilterUpdated((undefined as unknown) as string)
  }, [onNameFilterUpdated])

  const clearAllFilters = useCallback(() => {
    setDateOfBirthInput('')
    onClearAllFilters?.()
  }, [onClearAllFilters])

  const onBirthSexSelect = useCallback(
    (
      event: React.MouseEvent | React.ChangeEvent,
      selection: string | SelectOptionObject,
    ) => {
      if (
        (event.target as HTMLInputElement | undefined)?.checked !== undefined &&
        typeof selection === 'string'
      ) {
        const checked = (event.target as HTMLInputElement).checked
        const prevSelections = birthSex ?? []
        const updatedBirthSex = checked
          ? [...prevSelections, selection]
          : prevSelections.filter((v) => v !== selection)
        onBirthSexUpdated(updatedBirthSex)
      }
    },
    [birthSex, onBirthSexUpdated],
  )

  const onBirthSexToggle = useCallback((isOpen: boolean) => {
    setIsBirthSexDropdownOpen(isOpen)
  }, [])

  const onDeleteBirthSex = useCallback(
    (_: unknown, value: string | ToolbarChip) => {
      const toRemove = String(value)
      const updatedBirthSex = (birthSex ?? []).filter((it) => it !== toRemove)
      onBirthSexUpdated(updatedBirthSex)
    },
    [birthSex, onBirthSexUpdated],
  )

  const updateDateOfBirth = useCallback(
    (value: string) => {
      setDateOfBirthInput(value)
      if (onDateOfBirthUpdated) {
        onDateOfBirthUpdated(value || ((undefined as unknown) as string))
      }
    },
    [onDateOfBirthUpdated],
  )

  const onDeleteDateOfBirth = useCallback(() => {
    setDateOfBirthInput('')
    onDateOfBirthUpdated?.((undefined as unknown) as string)
  }, [onDateOfBirthUpdated])

  const onDeleteDeliverable = useCallback(() => {
    onDeliverableFilterUpdated?.(undefined)
  }, [onDeliverableFilterUpdated])

  const onDeliverableToggle = useCallback((isOpen: boolean) => {
    setIsDeliverableDropdownOpen(isOpen)
  }, [])

  const onDeliverableSelect = useCallback(
    (
      _event: React.MouseEvent | React.ChangeEvent,
      selection: string | SelectOptionObject,
    ) => {
      if (onDeliverableFilterUpdated) {
        if (selection === 'Deliverable') {
          onDeliverableFilterUpdated(true)
        } else if (selection === 'Undeliverable') {
          onDeliverableFilterUpdated(false)
        }
      }
    },
    [onDeliverableFilterUpdated],
  )

  const categoryDropdown = useMemo(
    () => (
      <ToolbarItem>
        <Dropdown
          onSelect={onCategorySelect}
          position={DropdownPosition.left}
          toggle={
            <DropdownToggle
              onToggle={onCategoryToggle}
              style={{ width: '100%' }}
            >
              <FilterIcon /> {FilterLabels[currentCategory]}
            </DropdownToggle>
          }
          isOpen={isCategoryDropdownOpen}
          dropdownItems={categoryDropdownItems}
          style={{ width: '100%' }}
        />
      </ToolbarItem>
    ),
    [
      categoryDropdownItems,
      currentCategory,
      isCategoryDropdownOpen,
      onCategorySelect,
      onCategoryToggle,
    ],
  )

  const filtersFragment = useMemo(
    () => (
      <>
        <ToolbarFilter
          chips={mrnFilterChips}
          deleteChip={onDeleteMrnFilter}
          categoryName="MRN"
          showToolbarItem={currentCategory === FilterCategories.MRN}
        >
          <InputGroup>
            <TextInput
              name="mrnInput"
              id="mrnInput1"
              type="search"
              aria-label="mrn filter"
              onChange={onMrnFilterUpdated}
              value={mrnFilter || ''}
              placeholder="Filter by MRN..."
            />
          </InputGroup>
        </ToolbarFilter>

        <ToolbarFilter
          chips={nameFilterChips}
          deleteChip={onDeleteNameFilter}
          categoryName="Name"
          showToolbarItem={currentCategory === FilterCategories.PatientName}
        >
          <InputGroup>
            <TextInput
              name="nameInput"
              id="nameInput1"
              type="search"
              aria-label="name filter"
              onChange={onNameFilterUpdated}
              value={nameFilter || ''}
              placeholder="Filter by Name..."
            />
          </InputGroup>
        </ToolbarFilter>

        <ToolbarFilter
          chips={birthSex ?? []}
          deleteChip={onDeleteBirthSex}
          categoryName={FilterLabels[FilterCategories.BirthSex]}
          showToolbarItem={currentCategory === FilterCategories.BirthSex}
        >
          <Select
            variant={SelectVariant.checkbox}
            aria-label={FilterLabels[FilterCategories.BirthSex]}
            onToggle={onBirthSexToggle}
            onSelect={onBirthSexSelect}
            selections={birthSex}
            isOpen={isBirthSexDropdownOpen}
            placeholderText="Filter by Birth Sex..."
          >
            <SelectOption key="male" value="Male" />
            <SelectOption key="female" value="Female" />
            <SelectOption key="other" value="Other" />
            {/* NOTE: trailing space is preserved intentionally to avoid behavior change */}
            <SelectOption key="unknown" value="Unknown " />
          </Select>
        </ToolbarFilter>

        <ToolbarFilter
          chips={dateOfBirthFilterChips}
          deleteChip={onDeleteDateOfBirth}
          categoryName={FilterLabels[FilterCategories.BirthDate]}
          showToolbarItem={currentCategory === FilterCategories.BirthDate}
        >
          <TextInput
            isRequired
            type="date"
            onChange={updateDateOfBirth}
            value={dateOfBirthInput}
            max={maxDate}
          />
        </ToolbarFilter>

        <ToolbarFilter
          chips={deliverableFilterChips}
          deleteChip={onDeleteDeliverable}
          categoryName={FilterLabels[FilterCategories.Deliverable]}
          showToolbarItem={currentCategory === FilterCategories.Deliverable}
        >
          <Select
            variant={SelectVariant.single}
            aria-label={FilterLabels[FilterCategories.Deliverable]}
            onToggle={onDeliverableToggle}
            onSelect={onDeliverableSelect}
            selections={
              deliverableFilter === undefined
                ? undefined
                : deliverableFilter
                ? 'Deliverable'
                : 'Undeliverable'
            }
            isOpen={isDeliverableDropdownOpen}
            placeholderText="Filter by Communication Status..."
          >
            <SelectOption key="deliverable" value="Deliverable" />
            <SelectOption key="undeliverable" value="Undeliverable" />
          </Select>
        </ToolbarFilter>
      </>
    ),
    [
      birthSex,
      currentCategory,
      dateOfBirthFilterChips,
      dateOfBirthInput,
      deliverableFilter,
      deliverableFilterChips,
      isBirthSexDropdownOpen,
      isDeliverableDropdownOpen,
      maxDate,
      mrnFilter,
      mrnFilterChips,
      nameFilter,
      nameFilterChips,
      onBirthSexSelect,
      onBirthSexToggle,
      onDeliverableSelect,
      onDeliverableToggle,
      onDeleteBirthSex,
      onDeleteDateOfBirth,
      onDeleteDeliverable,
      onDeleteMrnFilter,
      onDeleteNameFilter,
      onMrnFilterUpdated,
      onNameFilterUpdated,
      updateDateOfBirth,
    ],
  )

  return (
    <Toolbar
      id="toolbar-with-chip-groups"
      clearAllFilters={clearAllFilters}
      collapseListedFiltersBreakpoint="xl"
    >
      {onPrimaryAction && primaryActionLabel && (
        <ToolbarContent>
          <ToolbarItem>
            <Button variant="primary" onClick={onPrimaryAction}>
              {primaryActionLabel}
            </Button>
          </ToolbarItem>
        </ToolbarContent>
      )}

      <ToolbarContent>
        <ToolbarItem>
          <Button
            variant={ButtonVariant.control}
            onClick={onRefresh}
            aria-label="Refresh"
          >
            <SyncAltIcon />
          </Button>
        </ToolbarItem>

        <ToolbarToggleGroup toggleIcon={<FilterIcon />} breakpoint="xl">
          <ToolbarGroup variant="filter-group">
            {categoryDropdown}
            {filtersFragment}
          </ToolbarGroup>
        </ToolbarToggleGroup>

        <ToolbarItem variant="pagination" alignment={{ default: 'alignRight' }}>
          <Pagination
            itemCount={totalNumberOfRecords}
            onBlur={handleCurrentPageChanged}
            onChange={handleCurrentPageChanged}
            onPerPageSelect={handlePerPageSelect}
            onSetPage={handleSetPage}
            page={pageNumber}
            perPage={recordsPerPage}
            widgetId="pagination-options-menu-bottom"
          />
        </ToolbarItem>
      </ToolbarContent>
    </Toolbar>
  )
}
